import React, { useEffect, useState } from 'react'
import { amountFormatter } from '/src/lib/helpers'
import { formatDisclosedAmount, getVintageLabel, computeExitRateMeta } from '/src/components/ICESections/Shared/helpers'
import { PUBLIC_IMG_URL } from '/src/lib/endpoints';
import Skeleton from 'react-loading-skeleton';
import investorApi from '/src/apis/investorApi';
import Highlights from '/src/components/Reusable/Highlights/Highlights';

const ICEHighlights = ({ entity, user, showInfo, isEntityLoading, entityId, entityType }) => {
    const [aggregationData, setAggregationData] = useState({
        vintageRange: undefined,
        totalTargetFundSize: null,
        avgFundSize: null,
        totalAmountRaised: null,
    })
    const [isAggregationLoading, setIsAggregationLoading] = useState(true)

    useEffect(() => {
        if (entityId && entityType) {
            investorApi
                .getManagedFundAggregation(entityId, entityType)
                .then((data) => setAggregationData({
                    vintageRange: data?.vintage_range ?? null,
                    totalTargetFundSize: data?.total_target_fund_size ?? null,
                    avgFundSize: data?.avg_fund_size ?? null,
                    totalAmountRaised: data?.total_amount_raised?.amount ?? null,
                }))
                .catch(() => setAggregationData({
                    vintageRange: null,
                    totalTargetFundSize: null,
                    avgFundSize: null,
                    totalAmountRaised: null,
                }))
                .finally(() => setIsAggregationLoading(false))
        } else {
            setIsAggregationLoading(false)
        }
    }, [entityId, entityType])

    if (!entity) {
        return null
    }

    const shouldRenderLockedPreview = user?.is_loggedin && !showInfo

    const numberOfFunds = entity.number_of_funds
    const numberOfLps = entity.number_of_lps
    const totalInvestmentsCount = entity.number_of_investments
    const exitsCount = entity.number_of_exits
    const numberOfEntities = entity.number_of_entities
    const totalTargetFundSize = aggregationData.totalTargetFundSize

    if (!shouldRenderLockedPreview && (isEntityLoading || isAggregationLoading)) {
        return (
            <div className="profile-section-box">
                <Skeleton width={180} height={22} style={{ marginBottom: 16 }} />
                <div className="highlights">
                    {[1, 2, 3, 4, 5].map(n => (
                        <div key={n} className="highlights__card">
                            <Skeleton width={90} height={12} style={{ marginBottom: 14 }} />
                            <Skeleton width={72} height={36} />
                            <Skeleton width={110} height={12} style={{ marginTop: 10 }} />
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    const hasFundsOrDeals = (numberOfFunds > 0) || (totalInvestmentsCount > 0)

    if (!shouldRenderLockedPreview && !hasFundsOrDeals) {
        return null
    }

    const formatAmount = (value) => {
        if (!value && value !== 0) return 'N/A'
        return amountFormatter(value)
    }

    const formatDisplay = (value, isAmount = true) => {
        if (value === undefined || value === null) return 'N/A'
        const raw = (isAmount & value != 0) ? formatAmount(value) : value
        return raw
    }

    const renderBlurredImage = (isAmount) => (
        <img
            src={`${PUBLIC_IMG_URL}/blurred_images/blur_mask.png`}
            style={{ height: '20px', width: isAmount ? '72px' : '40px' }}
            alt={isAmount ? 'Blurred amount value' : 'Blurred number value'}
            className="highlights__blurred-image"
        />
    )

    const fundsRaisedValue = formatDisclosedAmount(totalTargetFundSize, numberOfFunds)

    const items = [
        {
            key: 'funds_raised',
            label: 'Total Funds Raised',
            displayValue: showInfo ? fundsRaisedValue : renderBlurredImage(true),
            valueClass: fundsRaisedValue === 'Undisclosed' ? 'highlights__value--undisclosed' : '',
            meta: aggregationData.avgFundSize != null
                ? `Average fund size ${amountFormatter(aggregationData.avgFundSize)}`
                : null
        },
        {
            key: 'funds',
            label: 'Number of Funds',
            displayValue: showInfo ? formatDisplay(numberOfFunds, false) : renderBlurredImage(false),
            meta: aggregationData.vintageRange !== undefined ? getVintageLabel(aggregationData.vintageRange) : null,
        },
        {
            key: 'lps',
            label: 'Number of LPs',
            displayValue: showInfo ? formatDisplay(numberOfLps, false) : renderBlurredImage(false),
            meta: aggregationData.totalAmountRaised
                ? `${amountFormatter(aggregationData.totalAmountRaised)} committed`
                : 'Aggregate funds raised',
        },
        {
            key: 'total_investments',
            label: 'Total Investments',
            displayValue: showInfo ? formatDisplay(totalInvestmentsCount, false) : renderBlurredImage(false),
            meta: numberOfEntities ? `Across ${numberOfEntities} companies` : null,
        },
        {
            key: 'exits',
            label: 'Number of Exits',
            displayValue: showInfo ? formatDisplay(exitsCount, false) : renderBlurredImage(false),
            meta: computeExitRateMeta(exitsCount, numberOfEntities),
        },
    ]

    return (
        <div className={'profile-section-box' + (showInfo ? '' : ' paywall-text-blur')}>
            <h2 className="profile-section-heading">Investment Overview</h2>
            <Highlights items={items} />
        </div>
    )
}

export default ICEHighlights
